import { useCallback, useRef, useState } from 'react';
import CenteredAlert from './CenteredAlert';
import ConfirmDialog from './ConfirmDialog';
import { getApiErrorMessage } from '../../utils/apiError';

/**
 * Alertas centralizados + confirmação reutilizável.
 *
 * Uso:
 *   const { showSuccess, showApiError, askConfirm, FeedbackUi } = useFeedbackAlert();
 *   ...
 *   <FeedbackUi />
 */
export default function useFeedbackAlert() {
  const [alertState, setAlertState] = useState({
    open: false,
    variant: 'error',
    title: '',
    message: '',
  });

  const [confirmState, setConfirmState] = useState({
    open: false,
    title: 'Confirmar',
    message: '',
    confirmLabel: 'Confirmar',
    cancelLabel: 'Cancelar',
    confirmColor: 'primary',
    loading: false,
  });

  const confirmResolverRef = useRef(null);

  const closeAlert = useCallback(() => {
    setAlertState((prev) => ({ ...prev, open: false }));
  }, []);

  const showError = useCallback((message, title = '') => {
    setAlertState({
      open: true,
      variant: 'error',
      title,
      message: String(message || 'Ocorreu um erro.'),
    });
  }, []);

  const showSuccess = useCallback((message, title = '') => {
    setAlertState({
      open: true,
      variant: 'success',
      title,
      message: String(message || 'Operação concluída.'),
    });
  }, []);

  const showWarning = useCallback((message, title = '') => {
    setAlertState({
      open: true,
      variant: 'warning',
      title,
      message: String(message || 'Atenção.'),
    });
  }, []);

  const showApiError = useCallback((error, fallback = 'Não foi possível concluir a operação.') => {
    showError(getApiErrorMessage(error, fallback));
  }, [showError]);

  const askConfirm = useCallback(({
    title = 'Confirmar',
    message = 'Deseja continuar?',
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    confirmColor = 'primary',
  } = {}) => new Promise((resolve) => {
    confirmResolverRef.current = resolve;
    setConfirmState({
      open: true,
      title,
      message,
      confirmLabel,
      cancelLabel,
      confirmColor,
      loading: false,
    });
  }), []);

  const finishConfirm = useCallback((result) => {
    const resolve = confirmResolverRef.current;
    confirmResolverRef.current = null;
    setConfirmState((prev) => ({ ...prev, open: false, loading: false }));
    resolve?.(result);
  }, []);

  const setConfirmLoading = useCallback((loading) => {
    setConfirmState((prev) => ({ ...prev, loading }));
  }, []);

  const FeedbackUi = useCallback(() => (
    <>
      <CenteredAlert
        open={alertState.open}
        variant={alertState.variant}
        title={alertState.title}
        message={alertState.message}
        onClose={closeAlert}
      />
      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        confirmColor={confirmState.confirmColor}
        loading={confirmState.loading}
        onCancel={() => finishConfirm(false)}
        onConfirm={() => finishConfirm(true)}
      />
    </>
  ), [alertState, confirmState, closeAlert, finishConfirm]);

  return {
    showError,
    showSuccess,
    showWarning,
    showApiError,
    askConfirm,
    setConfirmLoading,
    FeedbackUi,
    closeAlert,
  };
}
